/*
  Swaps the collection product grid when a pill is tapped, without a full page
  load, by re-requesting the grid section for the target collection through
  Shopify's Section Rendering API.

  Falls back to ordinary link navigation whenever the swap cannot be done
  safely: no product grid on the page, no History API, a failed request, or a
  response that does not contain a grid (for example a collection whose
  template has no main-collection-product-grid section).
*/
class CollectionPills extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.collection-pills__track');
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    this.scrollActiveIntoView('auto');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
  }

  get gridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  get sectionId() {
    const section = this.gridContainer && this.gridContainer.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  onClick(event) {
    const pill = event.target.closest('.collection-pill');
    if (!pill) return;

    if (pill.classList.contains('collection-pill--active')) {
      event.preventDefault();
      return;
    }

    // Let the browser navigate normally when we cannot swap in place.
    if (!this.gridContainer || !this.sectionId || !window.history.pushState) return;

    event.preventDefault();
    this.setActive(pill);
    this.render(pill.href, true);
  }

  onPopState() {
    // Dawn's facets.js also listens for popstate, but it only re-renders when
    // the state carries a `searchParams` key. The state pushed below
    // deliberately omits it, so this element owns back/forward navigation.
    if (!this.gridContainer || !this.sectionId) return;

    const pill = this.pillForPath(window.location.pathname);
    if (pill) this.setActive(pill);
    this.render(window.location.href, false);
  }

  async render(url, updateHistory) {
    this.toggleLoading(true);

    try {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(sectionUrl.toString());
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = parsed.getElementById('ProductGridContainer');
      if (!grid) throw new Error('Response contained no product grid');

      this.gridContainer.innerHTML = grid.innerHTML;
      this.replaceFilters(parsed);

      if (updateHistory) {
        window.history.pushState({ collectionPills: true }, '', url);
      }

      this.refreshThirdPartyWidgets();
      document.dispatchEvent(new CustomEvent('collection-pills:loaded', { detail: { url } }));
    } catch (error) {
      console.error('[collection-pills]', error);
      window.location.href = url;
      return;
    }

    this.toggleLoading(false);
  }

  replaceFilters(parsed) {
    const current = document.getElementById('main-collection-filters');
    if (!current) return;

    const next = parsed.getElementById('main-collection-filters');
    // Filters belong to the collection being shown, so stale ones are cleared
    // even when the incoming collection has none.
    current.innerHTML = next ? next.innerHTML : '';
  }

  // Review badges arrive pre-rendered from the product metafield, but Judge.me
  // still binds behaviour to them on load.
  refreshThirdPartyWidgets() {
    if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
      try {
        window.jdgm.customizeBadges();
      } catch (error) {
        console.error('[collection-pills] Judge.me refresh failed', error);
      }
    }
  }

  pillForPath(pathname) {
    return Array.from(this.querySelectorAll('.collection-pill')).find(
      (pill) => new URL(pill.href, window.location.origin).pathname === pathname
    );
  }

  toggleLoading(isLoading) {
    const collection = this.gridContainer && this.gridContainer.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);
    this.classList.toggle('collection-pills--loading', isLoading);
  }

  setActive(pill) {
    this.querySelectorAll('.collection-pill').forEach((element) => {
      const isActive = element === pill;
      element.classList.toggle('collection-pill--active', isActive);
      if (isActive) {
        element.setAttribute('aria-current', 'page');
      } else {
        element.removeAttribute('aria-current');
      }
    });

    this.scrollActiveIntoView('smooth');
  }

  scrollActiveIntoView(behavior) {
    const active = this.querySelector('.collection-pill--active');
    if (!active || !this.track) return;

    const offset = active.offsetLeft - (this.track.clientWidth - active.offsetWidth) / 2;
    this.track.scrollTo({ left: Math.max(offset, 0), behavior });
  }
}

if (!customElements.get('collection-pills')) {
  customElements.define('collection-pills', CollectionPills);
}
